/**
 * AuthPage — dedicated sign-up / sign-in screen. Replaces the inline
 * auth block that used to live inside the landing hero.
 *
 * v146.10 — Split-out so:
 *   - Every "Sign up / Create Bio / Sign up in 5 minutes" CTA on the
 *     landing page navigates here instead of trying to scroll to
 *     `#signin` on the same page.
 *   - Logging out lands here (not on the marketing landing) so returning
 *     users can sign back in with one tap.
 *   - The layout is intentionally large — big buttons, big input
 *     targets, a required Terms & Conditions checkbox — so first-time
 *     visitors on mobile can read and act without pinching to zoom.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import AuthTabs from "../components/AuthTabs";
import FirebaseAuthButtons from "../components/FirebaseAuthButtons";
import LinkolaMark from "../components/LinkolaMark";
import { externalLinkClickHandler } from "../lib/openExternal";
import { useAuth } from "../context/AuthContext";
import { useAppFlags } from "../lib/useAppFlags";
import { useIsNativeApp } from "../lib/useIsNativeApp";
import { trackSiteEvent } from "../lib/siteTrack";

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { flag, loaded: flagsLoaded } = useAppFlags();
  // v147.99.20 — Skip the marketing header + "Back to home" chrome
  // inside the Capacitor iOS/Android shell. On mobile web the header
  // gives users a way back to /; in the native app that link routes
  // to the same webview root (dead-end) and reads as marketing noise
  // during signup.
  const isNativeApp = useIsNativeApp();
  // v146.17 — Terms gate is now signup-only. Returning users signing in
  // aren't re-shown the checkbox (they already agreed at signup).
  // `accepted` defaults to true in login mode so the auth methods below
  // aren't dimmed/disabled.
  const isLoginMode = useMemo(() => {
    const p = new URLSearchParams(location.search);
    if (p.get("mode") === "login") return true;
    if ((location.pathname || "").toLowerCase().endsWith("/signin")) return true;
    return false;
  }, [location.search, location.pathname]);
  // v147.99.40 — If an already-authed user lands here (native back-swipe
  // can pop the WebView all the way back to /signin), bounce them into
  // /app instead of forcing a re-login. Session cookie is still valid.
  useEffect(() => {
    if (user) navigate("/app", { replace: true });
  }, [user, navigate]);
  const [accepted, setAccepted] = useState(isLoginMode);
  // v146.89 — Track the AuthTabs inner mode so if the user starts on
  // signup then toggles to "Sign in", we also hide the Terms card.
  const [childMode, setChildMode] = useState(isLoginMode ? "login" : "signup");
  const inLoginMode = isLoginMode || childMode === "login";
  // Keep `accepted` in sync if the user toggles mode inside the same
  // AuthTabs session (e.g. taps "New here? Create an account").
  useEffect(() => { if (isLoginMode) setAccepted(true); }, [isLoginMode]);
  useEffect(() => { if (childMode === "login") setAccepted(true); }, [childMode]);
  // v146.13 — Super-admin controlled auth-method visibility.
  const phoneOn = flag("auth_phone_enabled");
  const emailOn = flag("auth_email_enabled");
  const googleOn = flag("auth_google_enabled");
  const appleOn = flag("auth_apple_enabled");
  // Apple button only shows on iOS Capacitor builds; on web/Android it's
  // never visible even when the flag is ON. Treat it as OFF on those
  // platforms so the "or continue with" divider doesn't get orphaned.
  const isIOSNative = typeof window !== "undefined"
    && window.Capacitor?.isNativePlatform?.()
    && window.Capacitor?.getPlatform?.() === "ios";
  const anyNativeTab = phoneOn || emailOn;
  const anySocialBtn = googleOn || (appleOn && isIOSNative);
  // Query param `?mode=login` — legacy trigger from the landing nav.
  // The dedicated `/signin` route also defaults to sign-in mode so a
  // user landing directly on that URL sees the login form. Everywhere
  // else we default to sign-up (~90% of arrivals are new users).
  const initialMode = isLoginMode ? "login" : "signup";

  useEffect(() => {
    trackSiteEvent("auth_page_view", { mode: initialMode });
  }, [initialMode]);

  useEffect(() => {
    // Already signed in — honour `?clone_after_signin=<slug>` so signup
    // originating from a bio "Copy this design" ribbon returns to the
    // source hub with the preview modal auto-opening. Otherwise land
    // straight in the app.
    // v147.99.24 — Send *fresh* signups to /onboarding for the role
    // picker; grandfather existing users (who already have a name /
    // creator_slug from the pre-picker era) straight to /app so we
    // don't re-nag them through a wizard they've already finished.
    if (!user) return;
    const cloneSlug = new URLSearchParams(location.search).get("clone_after_signin");
    if (cloneSlug) {
      navigate(`/u/${encodeURIComponent(cloneSlug)}?open_clone_preview=1`, { replace: true });
      return;
    }
    // Only a truly fresh signup (no name, no slug, no view_mode) is
    // routed through the wizard. Login users always skip.
    const isFreshSignup = !inLoginMode && !user.view_mode && !user.creator_slug && !user.name;
    if (isFreshSignup) {
      navigate("/onboarding", { replace: true });
      return;
    }
    navigate("/app", { replace: true });
  }, [user, navigate, location.search, inLoginMode]);

  return (
    <div className="min-h-screen bg-page text-ink flex flex-col">
      {/* Header — logo returns to landing, Back button for mobile.
          v147.99.20 — Hidden inside the Capacitor native shell so the
          signup screen reads like a native onboarding (no back-to-
          marketing-site affordance that leads nowhere on iOS/Android). */}
      {!isNativeApp && (
      <header className="border-b-2 border-ink bg-page sticky top-0 z-30 pt-[env(safe-area-inset-top,0)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            data-testid="authpage-logo"
            aria-label="Back to Linkola home"
          >
            <LinkolaMark size={44} />
            <span className="font-display font-black text-xl sm:text-2xl tracking-tighter">Linkola</span>
          </button>
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-ink/70 hover:text-ink"
            data-testid="authpage-back"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={3} />
            <span className="hidden sm:inline">Back to home</span>
          </button>
        </div>
      </header>
      )}

      {/* Main card */}
      {/* v147.99.40 — On native shell, the root `.min-h-screen` picks up
          safe-area padding automatically via the global CSS safety net
          in index.css, so no inline override needed here. */}
      <main className="flex-1 w-full max-w-xl mx-auto px-4 sm:px-6 py-8 sm:py-14">
        <div className="text-center mb-8 sm:mb-10">
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none">
            {inLoginMode ? "Welcome back" : "Create your account"}
          </h1>
          <p className="mt-3 text-base sm:text-lg text-ink/70 max-w-md mx-auto">
            {inLoginMode
              ? "Sign in to your Linkola account."
              : "Set up your /u/ link and Action Pages in under 5 minutes. No credit card needed."}
          </p>
        </div>

        {/* Terms & Conditions gate — required before signup. Login users
            already agreed at signup so the card is hidden entirely for
            them (v146.17). Kept as a loud coloured card while unchecked
            in signup mode so first-time visitors can't miss it. */}
        {!inLoginMode && (
          <div
            className={`border-2 border-ink p-4 sm:p-5 mb-6 transition-colors ${
              accepted ? "bg-cream/40" : "bg-sun/40"
            }`}
            data-testid="authpage-terms-card"
          >
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-1 w-5 h-5 shrink-0 accent-coral"
                data-testid="authpage-terms-checkbox"
              />
              <span className="text-sm sm:text-base leading-relaxed">
                I agree to Linkola&apos;s{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer"
                   onClick={externalLinkClickHandler("/terms")}
                   className="font-bold underline underline-offset-2 hover:text-coral">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer"
                   onClick={externalLinkClickHandler("/privacy")}
                   className="font-bold underline underline-offset-2 hover:text-coral">
                  Privacy Policy
                </a>.
              </span>
            </label>
            <p className="mt-3 text-xs text-ink/60">
              Returning user?{" "}
              <button
                type="button"
                onClick={() => navigate("/signin")}
                data-testid="authpage-goto-signin"
                className="font-bold underline underline-offset-2 hover:text-coral"
              >
                Sign in instead
              </button>{" "}
              — no need to accept the Terms again.
            </p>
          </div>
        )}

        {/* Auth methods — dimmed until Terms is accepted. Pointer events
            are blocked (not just visually greyed) so users can't tab into
            an input and start typing before acknowledging Terms.
            v146.13 — Each block hides individually when the Super Admin
            disables that method. When every method is off we render a
            "contact support" fallback so the page never looks broken.
            v146.89 — Escape hatch: an "already have an account? Sign in"
            link is rendered ABOVE the Terms card so returning users can
            reach the sign-in form without first accepting Terms. */}
        <div
          className={`space-y-5 transition-opacity ${accepted ? "opacity-100" : "opacity-40 pointer-events-none select-none"}`}
          aria-disabled={!accepted}
          data-testid="authpage-methods"
        >
          {anyNativeTab && (
            <div className="border-2 border-ink bg-white p-4 sm:p-5 shadow-brutalSm" data-testid="authpage-native-block">
              <p className="text-xs font-black uppercase tracking-widest text-ink/60 mb-3">
                {phoneOn && emailOn ? "Phone or Email" : phoneOn ? "Phone number" : "Email address"}
              </p>
              <AuthTabs
                redirectTo="/app"
                defaultTab={phoneOn ? "phone" : "email"}
                initialMode={initialMode}
                phoneEnabled={phoneOn}
                emailEnabled={emailOn}
                onModeChange={setChildMode}
              />
            </div>
          )}

          {anyNativeTab && anySocialBtn && (
            <div className="flex items-center gap-3" data-testid="authpage-or-divider">
              <div className="flex-1 h-[2px] bg-ink/20" />
              <span className="text-[11px] font-bold uppercase tracking-widest text-ink/40">
                or continue with
              </span>
              <div className="flex-1 h-[2px] bg-ink/20" />
            </div>
          )}

          {anySocialBtn && (
            <div className="border-2 border-ink bg-white p-4 sm:p-5 shadow-brutalSm" data-testid="authpage-social-block">
              <FirebaseAuthButtons
                redirectTo="/app"
                googleEnabled={googleOn}
                appleEnabled={appleOn}
              />
            </div>
          )}

          {flagsLoaded && !anyNativeTab && !anySocialBtn && (
            <div
              className="border-2 border-ink bg-sun p-4 sm:p-5 text-sm font-bold"
              data-testid="authpage-no-methods"
            >
              Sign-in is temporarily unavailable. Please check back shortly or
              reach us through the support link on the home page.
            </div>
          )}
        </div>

        {/* Legal footnote — smaller, secondary */}
        <p className="mt-8 text-xs text-ink/50 text-center leading-relaxed">
          By continuing, Linkola may send you occasional product updates.
          Unsubscribe anytime. We never sell your data.
        </p>
      </main>
    </div>
  );
}
